import { useEffect, useState, useCallback } from "react";
import { Accessibility, Type, Eye, Volume2, RotateCcw, X, Contrast, Underline } from "lucide-react";

/**
 * Widget Accessibilita' — ideale per clientela senior.
 * Pulsante floating bottom-left che apre un pannello con:
 * - Aumenta/riduci font (90%, 100%, 115%, 130%)
 * - Alto contrasto (filter: contrast)
 * - Sottolinea link
 * - Leggi ad alta voce (Web Speech API - TTS browser nativo)
 * - Reset
 *
 * Preferenze salvate in localStorage e riapplicate al reload.
 */

const STORAGE = "a11y-prefs";
const DEFAULT_PREFS = { fontScale: 1, highContrast: false, underlineLinks: false };

function applyPrefs(p) {
  const root = document.documentElement;
  root.style.fontSize = `${p.fontScale * 100}%`;
  if (p.highContrast) {
    root.classList.add("a11y-contrast");
  } else {
    root.classList.remove("a11y-contrast");
  }
  if (p.underlineLinks) {
    root.classList.add("a11y-underline-links");
  } else {
    root.classList.remove("a11y-underline-links");
  }
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE);
    if (saved) {
      try {
        const parsed = { ...DEFAULT_PREFS, ...JSON.parse(saved) };
        setPrefs(parsed);
        applyPrefs(parsed);
      } catch {}
    }
  }, []);

  const updatePrefs = useCallback((patch) => {
    setPrefs(p => {
      const next = { ...p, ...patch };
      localStorage.setItem(STORAGE, JSON.stringify(next));
      applyPrefs(next);
      return next;
    });
  }, []);

  const reset = () => {
    localStorage.removeItem(STORAGE);
    setPrefs(DEFAULT_PREFS);
    applyPrefs(DEFAULT_PREFS);
  };

  const toggleRead = () => {
    if (!("speechSynthesis" in window)) return;
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    // Estrae testo principale della pagina (solo main/article, salta UI e footer)
    const scope = document.querySelector("main") || document.body;
    const text = scope.innerText.slice(0, 4000); // limit
    if (!text) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "it-IT";
    u.rate = 0.95;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(u);
    setSpeaking(true);
  };

  const fontOptions = [
    { label: "A−", val: 0.9 },
    { label: "A", val: 1 },
    { label: "A+", val: 1.15 },
    { label: "A++", val: 1.3 },
  ];

  return (
    <>
      {/* Global styles for a11y classes */}
      <style>{`
        html.a11y-contrast { filter: contrast(1.25) saturate(1.15); }
        html.a11y-contrast body { background-color: #fff; }
        html.a11y-underline-links a:not([class*="rounded-full"]):not([role="button"]) {
          text-decoration: underline !important;
          text-underline-offset: 2px;
        }
        .a11y-btn { transition: all 0.15s ease; }
        .a11y-btn:focus-visible { outline: 3px solid #D97B58; outline-offset: 2px; }
      `}</style>

      {/* Floating trigger (bottom-left, not colliding with WhatsApp) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label="Strumenti di accessibilita'"
          data-testid="a11y-toggle"
          className="fixed bottom-6 left-6 z-50 w-12 h-12 rounded-full bg-[#0F1F14] text-white shadow-xl hover:bg-[#1d4a2e] hover:scale-105 transition-all flex items-center justify-center a11y-btn">
          <Accessibility className="w-5 h-5" strokeWidth={2.2} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 left-6 z-50 bg-white rounded-2xl shadow-2xl border border-zinc-200 w-[300px] overflow-hidden"
          role="dialog"
          aria-label="Strumenti di accessibilita'"
          data-testid="a11y-panel">
          <div className="bg-gradient-to-br from-[#0F1F14] to-[#1d4a2e] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-[#D97B58]" />
              <h3 className="font-heading text-base font-bold">Accessibilita&apos;</h3>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Chiudi pannello"
              className="text-white/70 hover:text-white a11y-btn" data-testid="a11y-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-4">
            {/* Font size */}
            <div>
              <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                <Type className="w-3.5 h-3.5" /> Dimensione testo
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {fontOptions.map(o => (
                  <button key={o.label}
                    onClick={() => updatePrefs({ fontScale: o.val })}
                    className={`a11y-btn h-10 rounded-lg text-sm font-bold border ${
                      Math.abs(prefs.fontScale - o.val) < 0.01
                        ? "bg-[#0F1F14] text-white border-[#0F1F14]"
                        : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid={`a11y-font-${o.val}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-2">
              <button
                onClick={() => updatePrefs({ highContrast: !prefs.highContrast })}
                className={`a11y-btn w-full flex items-center justify-between p-3 rounded-lg border ${prefs.highContrast ? "bg-[#0F1F14] text-white border-[#0F1F14]" : "bg-white border-zinc-200 hover:border-zinc-300"}`}
                data-testid="a11y-contrast">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <Contrast className="w-4 h-4" /> Alto contrasto
                </span>
                <span className={`w-9 h-5 rounded-full relative transition-colors ${prefs.highContrast ? "bg-[#D97B58]" : "bg-zinc-300"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${prefs.highContrast ? "translate-x-4" : "translate-x-0.5"}`} />
                </span>
              </button>

              <button
                onClick={() => updatePrefs({ underlineLinks: !prefs.underlineLinks })}
                className={`a11y-btn w-full flex items-center justify-between p-3 rounded-lg border ${prefs.underlineLinks ? "bg-[#0F1F14] text-white border-[#0F1F14]" : "bg-white border-zinc-200 hover:border-zinc-300"}`}
                data-testid="a11y-underline">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <Underline className="w-4 h-4" /> Sottolinea link
                </span>
                <span className={`w-9 h-5 rounded-full relative transition-colors ${prefs.underlineLinks ? "bg-[#D97B58]" : "bg-zinc-300"}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${prefs.underlineLinks ? "translate-x-4" : "translate-x-0.5"}`} />
                </span>
              </button>

              <button
                onClick={toggleRead}
                className={`a11y-btn w-full flex items-center justify-between p-3 rounded-lg border ${speaking ? "bg-[#D97B58] text-white border-[#D97B58]" : "bg-white border-zinc-200 hover:border-zinc-300"}`}
                data-testid="a11y-read">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  <Volume2 className={`w-4 h-4 ${speaking ? "animate-pulse" : ""}`} />
                  {speaking ? "Sto leggendo..." : "Leggi la pagina"}
                </span>
              </button>
            </div>

            {/* Reset */}
            <button onClick={reset}
              className="a11y-btn w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border border-zinc-200 text-xs text-zinc-600 hover:bg-zinc-50 hover:border-zinc-300"
              data-testid="a11y-reset">
              <RotateCcw className="w-3.5 h-3.5" /> Ripristina impostazioni
            </button>

            <p className="text-[10px] text-zinc-400 text-center">
              Le tue preferenze vengono salvate nel browser per le visite future.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
